/**
 * Conteúdo do estilo ESTUDAR (estudar brincando).
 *
 * Feito para agenda infantil e para quem estuda com criança em casa: cada dia
 * traz **um desafio** (com a pista de como chegar lá, que é onde está o ensino) e
 * **uma palavra nova** para usar numa frase. Nada de prova nem de resposta
 * escondida: o desafio é para fazer junto, olhando a casa, a rua, o relógio e o
 * próprio corpo.
 *
 * Três cuidados valem para tudo aqui:
 *
 * 1. o **texto é original**, escrito para este produto — nenhum exercício copiado
 *    de livro didático, apostila ou site de escola;
 * 2. o desafio é **fazível em dez minutos**, sem material especial: o que se pede
 *    está na mesa, no quintal, na janela ou na cabeça;
 * 3. a pista **ensina o caminho**, e não entrega o resultado — é a diferença
 *    entre "a resposta é 56" e "conte de sete em sete".
 *
 * Cada entrada serve dois quadros do dia: `DESAFIO_DO_DIA` (tema, desafio e
 * pista) e `PALAVRA_NOVA` (a palavra, o que ela quer dizer e a linha para usar
 * numa frase). O dia do miolo escolhe a entrada por
 * `índice % DESAFIOS.length`, então o mesmo período reimpresso traz exatamente os
 * mesmos desafios.
 */
export const DESAFIOS = [
    {
        tema: 'Tabuada',
        desafio: 'Quanto é 7 × 8? Chegue lá sem decorar a resposta.',
        pista: 'Some 8 sete vezes, ou 7 oito vezes. Conte nos dedos e confira.',
        palavra: 'dobro',
        significado: 'Duas vezes a mesma quantidade — o dobro de 12 é 24.',
    },
    {
        tema: 'Letras',
        desafio: 'Ache cinco coisas na cozinha que começam com a letra B.',
        pista: 'Diga o nome de tudo em voz alta; quem procura calado acha menos.',
        palavra: 'lista',
        significado: 'Uma sequência de nomes escritos um abaixo do outro.',
    },
    {
        tema: 'Medidas',
        desafio: 'Meça a mesa com os seus palmos e depois com uma régua.',
        pista: 'Mão aberta, do polegar ao dedinho. Os dois números vão dar diferente.',
        palavra: 'palmo',
        significado: 'A largura da mão aberta usada como medida antiga.',
    },
    {
        tema: 'Relógio',
        desafio: 'Que horas são? Escreva do jeito que o relógio mostra e do jeito que se fala.',
        pista: 'Ponteiro curto é a hora, o comprido são os minutos. Duas e meia é 2:30.',
        palavra: 'ponteiro',
        significado: 'A agulha do relógio, que gira para marcar as horas.',
    },
    {
        tema: 'Animais',
        desafio: 'Escolha um bicho do quintal ou da rua e conte como ele se move.',
        pista: 'Olhe as pernas, as asas e a cauda: cada um resolve o andar de um jeito.',
        palavra: 'rastejar',
        significado: 'Andar com a barriga encostada no chão, como a lagartixa.',
    },
    {
        tema: 'Rimas',
        desafio: 'Faça rimar quatro palavras que você falou hoje.',
        pista: 'Rima é o som igual no fim: coração com canção, pão com chão.',
        palavra: 'rima',
        significado: 'O som parecido no fim de duas palavras.',
    },
    {
        tema: 'Água',
        desafio: 'Encha um copo e diga onde a água estava antes de chegar na torneira.',
        pista: 'Siga o caminho: chuva, rio, tratamento, cano, caixa d’água.',
        palavra: 'nascente',
        significado: 'O lugar onde a água brota da terra e vira riacho.',
    },
    {
        tema: 'Sombra',
        desafio: 'Marque com giz onde está a sua sombra agora. Volte depois do almoço.',
        pista: 'A sombra anda porque a luz do sol vem de outro lado.',
        palavra: 'sombra',
        significado: 'A parte escura que aparece quando algo tapa a luz.',
    },
    {
        tema: 'Números',
        desafio: 'Escreva de 1 a 20 e circule só os números pares.',
        pista: 'Par é o que se divide em dois times iguais, sem sobrar ninguém.',
        palavra: 'par',
        significado: 'Número que pode ser dividido em duas partes iguais.',
    },
    {
        tema: 'Mapa',
        desafio: 'Desenhe o caminho da sua casa até a escola ou até a padaria.',
        pista: 'Coloque os pontos que você passa e o que fica de cada lado.',
        palavra: 'trajeto',
        significado: 'O caminho que se percorre de um lugar até outro.',
    },
    {
        tema: 'Corpo',
        desafio: 'Conte quantas vezes o seu coração bate em quinze segundos.',
        pista: 'Dois dedos no pulso, embaixo do polegar. Multiplique por quatro.',
        palavra: 'pulso',
        significado: 'O lugar do corpo onde se sente o coração batendo.',
    },
    {
        tema: 'Frutas',
        desafio: 'Separe as frutas da semana em doces e azedas. Qual ganhou?',
        pista: 'O azedo faz a boca apertar; o doce faz sorrir. Prove devagar.',
        palavra: 'azedo',
        significado: 'O gosto que aperta a boca, como o do limão.',
    },
    {
        tema: 'Sons',
        desafio: 'Fique um minuto calado e anote todos os sons que conseguir separar.',
        pista: 'Longe, perto, alto, baixo. Barulho é som que ninguém pediu.',
        palavra: 'eco',
        significado: 'O som que volta depois de bater numa parede ou num morro.',
    },
    {
        tema: 'Plantas',
        desafio: 'Escolha uma planta e descreva a folha dela pelo formato.',
        pista: 'Redonda, comprida, com ponta, com beirada serrilhada.',
        palavra: 'broto',
        significado: 'A pontinha nova que nasce na planta e ainda vai crescer.',
    },
    {
        tema: 'Casa',
        desafio: 'Ache na casa três coisas feitas de madeira e três de plástico.',
        pista: 'Bata de leve com a unha: a madeira soa seco, o plástico soa oco.',
        palavra: 'material',
        significado: 'Do que uma coisa é feita: papel, vidro, metal, tecido.',
    },
    {
        tema: 'Dinheiro',
        desafio: 'Com cinco reais, escreva três jeitos diferentes de juntar essa quantia.',
        pista: 'Use moedas de 1, 50 centavos e 25 centavos em combinações diferentes.',
        palavra: 'troco',
        significado: 'O dinheiro que volta quando se paga mais do que custou.',
    },
    {
        tema: 'Tempo',
        desafio: 'Quanto dura um minuto sem olhar o relógio? Feche os olhos e chute.',
        pista: 'Conte devagar até sessenta. Quase sempre o chute sai antes ou depois.',
        palavra: 'duração',
        significado: 'Quanto tempo uma coisa leva do começo ao fim.',
    },
    {
        tema: 'Palavras',
        desafio: 'Ache o oposto de: alto, cheio, claro, rápido e novo.',
        pista: 'Oposto é o que fica do outro lado: alto e baixo, cheio e vazio.',
        palavra: 'oposto',
        significado: 'O contrário de uma palavra: o oposto de quente é frio.',
    },
    {
        tema: 'Cores',
        desafio: 'Misture duas cores de tinta ou de lápis e diga o nome da cor que nasceu.',
        pista: 'Azul com amarelo dá verde; vermelho com amarelo dá laranja.',
        palavra: 'tonalidade',
        significado: 'O jeito da cor: mais clara, mais escura, mais viva.',
    },
    {
        tema: 'Contas',
        desafio: 'Some o preço de três coisas da feira e veja se dá com dez reais.',
        pista: 'Arredonde para o real mais perto e confira depois com os centavos.',
        palavra: 'soma',
        significado: 'O resultado de juntar duas ou mais quantias.',
    },
    {
        tema: 'Insetos',
        desafio: 'Quantas patas tem uma formiga? Conte numa que estiver parada.',
        pista: 'De um lado são três, do outro lado mais três. No meio tem a cintura.',
        palavra: 'antenado',
        significado: 'Quem presta atenção em tudo o que acontece ao redor.',
    },
    {
        tema: 'Livros',
        desafio: 'Abra um livro na página 10 e leia só a primeira frase em voz alta.',
        pista: 'Leia devagar e depois conte a mesma frase com as suas palavras.',
        palavra: 'parágrafo',
        significado: 'Um bloco de frases que trata do mesmo assunto.',
    },
    {
        tema: 'Ar',
        desafio: 'Prove que o ar existe sem falar — só com um gesto.',
        pista: 'Abane um leque de papel perto do rosto. O vento é ar em movimento.',
        palavra: 'vento',
        significado: 'O ar que se move de um lugar para o outro.',
    },
    {
        tema: 'Calendário',
        desafio: 'Ache a data de hoje e conte quantos dias faltam para o fim do mês.',
        pista: 'Veja quantos dias o mês tem e subtraia o número de hoje.',
        palavra: 'bimestre',
        significado: 'Um período de dois meses seguidos.',
    },
    {
        tema: 'História',
        desafio: 'Pergunte a alguém mais velho como era a escola quando ela era criança.',
        pista: 'Anote duas coisas que mudaram e uma que continua igual.',
        palavra: 'memória',
        significado: 'O que a gente guarda do que já aconteceu.',
    },
    {
        tema: 'Geografia',
        desafio: 'Diga o nome da sua rua, do seu bairro, da sua cidade e do seu estado.',
        pista: 'Do menor para o maior: rua, bairro, cidade, estado, país.',
        palavra: 'bairro',
        significado: 'A parte da cidade onde ficam as casas e as ruas perto da sua.',
    },
    {
        tema: 'Alfabeto',
        desafio: 'Escreva o alfabeto inteiro e marque as letras que só têm um som.',
        pista: 'O B, o D e o F mudam pouco; o X e o S mudam bastante.',
        palavra: 'sílaba',
        significado: 'Cada pedaço de som que a palavra solta ao ser falada.',
    },
    {
        tema: 'Frações',
        desafio: 'Divida uma fruta ou um pão em quatro partes iguais e coma uma.',
        pista: 'Quatro pedaços iguais: um é um quarto do total. Iguais é a parte difícil.',
        palavra: 'metade',
        significado: 'Uma das duas partes iguais de uma coisa inteira.',
    },
    {
        tema: 'Esporte',
        desafio: 'Conte quantos pulos você dá em um minuto. Tente de novo amanhã.',
        pista: 'Pule no mesmo ritmo; cansar no começo não é falta de força.',
        palavra: 'ritmo',
        significado: 'A repetição certa de um movimento, sempre no mesmo tempo.',
    },
    {
        tema: 'Alimentos',
        desafio: 'Escolha o que você comeu hoje e diga de onde cada coisa veio.',
        pista: 'Terra, árvore, animal, padaria, fábrica: quase tudo tem um começo.',
        palavra: 'colheita',
        significado: 'O tempo de tirar da terra o que foi plantado.',
    },
    {
        tema: 'Luz',
        desafio: 'Leve um espelho para o sol e jogue a luz na parede da sombra.',
        pista: 'A luz anda em linha reta e volta no espelho. Mire devagar.',
        palavra: 'reflexo',
        significado: 'A imagem que volta quando a luz bate no espelho ou na água.',
    },
    {
        tema: 'Tecnologia',
        desafio: 'Explique para alguém, sem apontar, o que é uma senha forte.',
        pista: 'Comprida, com letras e números, e que ninguém adivinha olhando você.',
        palavra: 'senha',
        significado: 'A palavra secreta que só você sabe e usa para entrar.',
    },
    {
        tema: 'Trânsito',
        desafio: 'Observe uma esquina e anote as três cores do semáforo na ordem certa.',
        pista: 'Vermelho para, amarelo espera, verde segue. Sempre nessa ordem.',
        palavra: 'faixa',
        significado: 'A parte da rua pintada para o pedestre atravessar.',
    },
    {
        tema: 'Reciclagem',
        desafio: 'Separe o lixo de hoje em papel, plástico, vidro e resto.',
        pista: 'Lave as embalagens antes de separar; sujo não se recicla.',
        palavra: 'reaproveitar',
        significado: 'Usar de novo uma coisa em vez de jogar fora.',
    },
    {
        tema: 'Música',
        desafio: 'Bata uma música conhecida só com as mãos na mesa.',
        pista: 'Marque primeiro o tempo forte, depois enfeite o meio.',
        palavra: 'compasso',
        significado: 'A divisão certinha do tempo de uma música.',
    },
    {
        tema: 'Estrelas',
        desafio: 'Escolha uma estrela ou a lua e desenhe a forma dela hoje à noite.',
        pista: 'Desenhe olhando; a lua muda de forma ao longo do mês.',
        palavra: 'fase',
        significado: 'Cada formato da lua: cheia, minguante, nova, crescente.',
    },
    {
        tema: 'Escola',
        desafio: 'Ensine uma coisa que você aprendeu esta semana para alguém da casa.',
        pista: 'Ensinar é explicar em voz alta até a outra pessoa entender sozinha.',
        palavra: 'explicar',
        significado: 'Falar de um jeito que a outra pessoa consiga entender.',
    },
    {
        tema: 'Sentidos',
        desafio: 'Com os olhos fechados, descubra três objetos só pelo tato.',
        pista: 'Sinta a forma, o peso e a temperatura antes de tentar o nome.',
        palavra: 'textura',
        significado: 'Como a superfície é ao toque: lisa, áspera, macia.',
    },
    {
        tema: 'Dias',
        desafio: 'Coloque em ordem os dias da semana e diga em que dia cai o próximo mês.',
        pista: 'Olhe o calendário: o dia 1 do mês que vem mostra o dia da semana.',
        palavra: 'quinzena',
        significado: 'Um período de quinze dias.',
    },
    {
        tema: 'Cozinha',
        desafio: 'Meça uma xícara de água e diga quantas xícaras enchem meio litro.',
        pista: 'Meio litro são 500 ml; uma xícara comum leva uns 240 ml.',
        palavra: 'receita',
        significado: 'A lista do que entra na comida e o passo a passo do preparo.',
    },
    {
        tema: 'Céu',
        desafio: 'Descreva o céu de hoje com três palavras que não sejam “bonito”.',
        pista: 'Fale das nuvens, da cor, do que o ar parece estar prometendo.',
        palavra: 'nublado',
        significado: 'Céu coberto de nuvens, com o sol escondido atrás delas.',
    },
    {
        tema: 'Talento',
        desafio: 'Escolha um animal e escreva uma coisa que ele faz melhor que você.',
        pista: 'Pense em ouvir, correr, cheirar, nadar ou dormir sem medo.',
        palavra: 'instinto',
        significado: 'O que o animal já sabe fazer sem ninguém ensinar.',
    },
];
//# sourceMappingURL=estudar.js.map